// Catálogo do seletor de modelo (camada 1): Izze · comerciais mais recentes · o resto ("Ver mais").
// Função pura, sem React: é o ponto de troca único da regra de curadoria (Q21) — quando as faixas e o
// Modo Desenvolvedor mudarem a fonte dos dados, só este arquivo muda; a UI segue lendo o mesmo formato.
// A fonte é a lista DO ESCOPO (GET /api/client/models?scope=), não mais a global de `me`.
import type { BandId, Me, ModelsResponse, OpenRouterModel, ScopeKind } from '../api';
import { LABELS } from '../labels';

export type CatalogItem = { id: string; name: string; provider?: string; vision: boolean; hay: string };

export type Catalog = {
  izze: CatalogItem;                  // id '' = o servidor escolhe (o chat envia model: null)
  orphan: CatalogItem | null;         // modelo salvo na conversa que o escopo não oferece mais
  featured: CatalogItem[];            // comerciais em destaque (mais novo de cada provedor)
  moreCommercial: CatalogItem[];      // demais comerciais liberados pelo operador
  open: CatalogItem[];                // modelos abertos do escopo (na Community: todos os nós ativos — Q5 = B)
  flat: boolean;                      // fora da Community não há comerciais: os modelos do escopo aparecem direto, sem "Ver mais"
  openTitle: string;                  // título da seção dos modelos abertos
  openEmpty: string;                  // texto quando não há nenhum
};

/** O que alimenta o catálogo: modelos abertos e comerciais de UM escopo. */
export type CatalogSource = { models: { id: string; vision: boolean }[]; openrouter: OpenRouterModel[] };

/** Quantos comerciais aparecem em destaque antes do "Ver mais". */
export const FEATURED_MAX = 6;

export const fold = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/** "OpenAI: GPT-4o" → provedor separado do nome, pra lista ficar escaneável. */
function splitName(full: string): { name: string; provider?: string } {
  const i = full.indexOf(': ');
  return i > 0 && i < 24 ? { provider: full.slice(0, i), name: full.slice(i + 2) } : { name: full };
}

const mk = (id: string, full: string, vision: boolean, extra = ''): CatalogItem => {
  const { name, provider } = splitName(full);
  return { id, name, provider, vision, hay: fold(`${full} ${id} ${extra} ${vision ? 'visão' : ''}`) };
};

/** Provedor do comercial: o campo `provider` da OpenRouter ou o 1º segmento do id ("openrouter/openai/gpt-5" → "openai"). */
const providerOf = (m: OpenRouterModel) => (m.provider || m.id.replace(/^openrouter\//, '').split('/')[0] || m.id).toLowerCase();

/**
 * HEURÍSTICA PROVISÓRIA (Q21): destaca o modelo mais novo (`created`) de cada provedor, do mais novo ao mais antigo.
 * A curadoria de verdade é o operador ligando/desligando modelos (`enabled`); um "-mini" recente pode aparecer no lugar
 * do principal. `created` ausente ordena por último; empate desempata pelo nome (resultado estável).
 */
function pickFeatured(list: OpenRouterModel[]): OpenRouterModel[] {
  const byNew = [...list].sort((a, b) => (b.created ?? -1) - (a.created ?? -1) || a.name.localeCompare(b.name));
  const seen = new Set<string>();
  const out: OpenRouterModel[] = [];
  for (const m of byNew) {
    const p = providerOf(m);
    if (seen.has(p)) continue;
    seen.add(p); out.push(m);
    if (out.length === FEATURED_MAX) break;
  }
  return out;
}

// ---------------------------------------------------------------- Modo Turbo: o valor do modelo escolhido pode ser uma faixa
/** `band:<família>:<small|medium|high>` — o mesmo formato que o servidor aceita em `model` (client-api/src/bands.js). */
const BAND_VALUE = /^band:([a-z0-9._-]{1,60}):(small|medium|high)$/;
export const bandValue = (family: string, band: BandId) => `band:${family}:${band}`;
export function parseBand(value: string): { family: string; band: BandId } | null {
  const m = BAND_VALUE.exec(value);
  return m ? { family: m[1], band: m[2] as BandId } : null;
}
export const bandLabel = (value: string): string | null => { const b = parseBand(value); return b ? `${b.family} · ${LABELS.bands[b.band]}` : null; };

const norm = (m: string) => m.replace(/:latest$/, '');

/** O modelo (ou faixa) existe no escopo? Izze sempre; faixa só com a faixa disponível; comercial só se liberado; aberto se algum nó o tem. */
export function modelInScope(value: string, d: ModelsResponse): boolean {
  if (value === '') return true;
  const b = parseBand(value);
  if (b) return Boolean(d.bands.find((f) => f.family === b.family)?.bands.find((x) => x.id === b.band)?.available);
  if (value.startsWith('openrouter/')) return d.openrouter.some((o) => o.id === value);
  return d.models.some((m) => norm(m.id) === norm(value));
}

/** Enquanto a lista do escopo não chega (ou falha) na Community, o seletor usa a lista global de `me` — é a mesma da Community. */
export const sourceFromMe = (me: Me): CatalogSource => ({ models: (me.models ?? []).map((id) => ({ id, vision: (me.visionModels ?? []).includes(id) })), openrouter: me.openrouter ?? [] });

export function buildCatalog(src: CatalogSource, value: string, opts: { kind?: ScopeKind; loading?: boolean } = {}): Catalog {
  const kind = opts.kind ?? 'community', flat = kind !== 'community';
  const commercial = src.openrouter ?? [];
  const featuredModels = pickFeatured(commercial);
  const featuredIds = new Set(featuredModels.map((m) => m.id));
  const toItem = (m: OpenRouterModel) => mk(m.id, m.name, m.vision);

  const izze = mk('', LABELS.izze, false, 'padrão');
  const featured = featuredModels.map(toItem);
  const moreCommercial = commercial.filter((m) => !featuredIds.has(m.id)).map(toItem);
  const open = src.models.map((m) => mk(m.id, m.id, m.vision));

  // modelo salvo na conversa que o escopo não oferece mais: continua visível pra dar pra trocar (faixa e carregamento não são "indisponível")
  const known = value === '' || opts.loading || parseBand(value) !== null || [...featured, ...moreCommercial, ...open].some((i) => i.id === value);
  const orphan = known ? null : { ...mk(value, value, false), name: `${value} (indisponível)` };

  const openTitle = kind === 'dedicated' || kind === 'onprem' ? 'Modelos desta pool' : 'Modelos abertos da rede';
  const openEmpty = flat ? 'Nenhum modelo disponível neste escopo agora' : 'Nenhum nó ativo agora';
  return { izze, orphan, featured, moreCommercial, open, flat, openTitle, openEmpty };
}

/** Todos os itens do catálogo, na ordem em que aparecem com "Ver mais" aberto (modelos da rede antes dos demais comerciais). */
export const allItems = (c: Catalog): CatalogItem[] => [c.izze, ...(c.orphan ? [c.orphan] : []), ...c.featured, ...c.open, ...c.moreCommercial];

/** O modelo está atrás do "Ver mais"? (o modal abre já expandido pra ele aparecer) Fora da Community não há "Ver mais". */
export const isHidden = (c: Catalog, id: string) => !c.flat && id !== '' && [...c.moreCommercial, ...c.open].some((i) => i.id === id);
